import { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import {
  ReportPreviewRenderReadyProvider,
  ReportPreviewRenderReadySignal,
} from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import AnalyticsExportPageRenderer from './AnalyticsExportPageRenderer';
import { ANALYTICS_EXPORT_ROOT_CLASS_NAME } from './constants/analyticsExportPageLayout';
import { useAnalyticsExportPages } from './useAnalyticsExportPages';

export type AnalyticsExportPublicMode = 'preview' | 'export' | 'present';

interface Props {
  preview: ReportPreviewPayload;
  mode?: AnalyticsExportPublicMode | null;
}

const AnalyticsExportPublicPage: FC<Props> = ({ preview, mode }) => {
  const { t } = useTranslation();
  const showPreviewWrapper = mode === 'preview';
  const pages = useAnalyticsExportPages(preview);

  return (
    <ReportPreviewRenderReadyProvider resetKey={preview.report.id}>
      <div
        className={clsx(
          ANALYTICS_EXPORT_ROOT_CLASS_NAME,
          'tw-h-full tw-w-full',
          showPreviewWrapper && 'tw-bg-grayscale-10 tw-px-16 tw-py-8',
        )}
      >
        <ReportPreviewRenderReadySignal />
        {pages.length === 0 ? (
          <p className="tw-py-8 tw-text-center tw-text-sm tw-text-grayscale-120">
            {t('analyticsReports.viewModal.noMetricsConfigured')}
          </p>
        ) : (
          <div
            className={clsx(
              'tw-w-full',
              showPreviewWrapper && 'tw-flex tw-flex-col tw-gap-8',
            )}
          >
            {pages.map((page, index) => (
              <AnalyticsExportPageRenderer
                key={page.key}
                page={page}
                pageNumber={index + 1}
                totalPages={pages.length}
              />
            ))}
          </div>
        )}
      </div>
    </ReportPreviewRenderReadyProvider>
  );
};

export default AnalyticsExportPublicPage;
